/**
 * Offline Map Cache Manager
 *
 * Manages downloading, storing, and retrieving offline map packages
 * using IndexedDB for persistent storage.
 */

const DB_NAME = 'QRMapsOfflineCache';
const DB_VERSION = 1;
const STORE_NAME = 'offlineMaps';

/**
 * Open IndexedDB connection
 */
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'mapId' });
        objectStore.createIndex('downloadedAt', 'downloadedAt', { unique: false });
        objectStore.createIndex('version', 'version', { unique: false });
      }
    };
  });
}

/**
 * Download offline package from S3
 * @param {string} url - S3 URL of the offline package
 * @param {Function} onProgress - Progress callback (percent)
 * @returns {Promise<ArrayBuffer>}
 */
export async function downloadOfflinePackage(url, onProgress) {
  console.log('offlineMapCache: fetching offline package', { url });
  const response = await fetch(url);

  if (!response.ok) {
    console.error('offlineMapCache: failed response', {
      url,
      status: response.status,
      statusText: response.statusText,
    });
    throw new Error(`Failed to download: ${response.statusText}`);
  }

  console.log('offlineMapCache: response headers', {
    url,
    status: response.status,
    contentLength: response.headers.get('content-length'),
    contentType: response.headers.get('content-type'),
    contentEncoding: response.headers.get('content-encoding'),
    acceptRanges: response.headers.get('accept-ranges'),
  });

  const contentLength = response.headers.get('content-length');
  const total = parseInt(contentLength, 10);

  if (!contentLength) {
    // No content-length header, just download normally
    return await response.arrayBuffer();
  }

  // Stream download with progress tracking
  const reader = response.body.getReader();
  const chunks = [];
  let receivedLength = 0;

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    chunks.push(value);
    receivedLength += value.length;

    if (onProgress) {
      const percent = (receivedLength / total) * 100;
      onProgress(percent);
    }
  }

  // Concatenate chunks into single ArrayBuffer
  const allChunks = new Uint8Array(receivedLength);
  let position = 0;
  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }

  return allChunks.buffer;
}

/**
 * Decompress gzipped data
 * @param {ArrayBuffer} compressedData - Gzipped data
 * @returns {Promise<ArrayBuffer>}
 */
export async function decompressGzip(compressedData) {
  const header = new Uint8Array(compressedData, 0, Math.min(8, compressedData.byteLength));
  const isGzip = header.length >= 2 && header[0] === 0x1f && header[1] === 0x8b;
  if (!isGzip) {
    console.warn('offlineMapCache: data is not gzip, skipping decompression', {
      header: Array.from(header),
      length: compressedData.byteLength,
    });
    return compressedData;
  }

  // Use DecompressionStream API (modern browsers)
  if (typeof DecompressionStream !== 'undefined') {
    const stream = new Response(compressedData)
      .body
      .pipeThrough(new DecompressionStream('gzip'));

    const decompressed = await new Response(stream).arrayBuffer();
    return decompressed;
  }

  // Fallback: use pako library if available
  if (window.pako) {
    const uint8Array = new Uint8Array(compressedData);
    const decompressed = window.pako.inflate(uint8Array);
    return decompressed.buffer;
  }

  throw new Error('No decompression method available. Need DecompressionStream or pako library.');
}

/**
 * Save offline package to IndexedDB
 * @param {number} mapId - Map ID
 * @param {ArrayBuffer} packageData - Decompressed package data
 * @param {Object} metadata - Additional metadata
 */
export async function saveOfflinePackage(mapId, packageData, metadata = {}) {
  const db = await openDatabase();

  const packageRecord = {
    mapId: mapId,
    data: packageData,
    downloadedAt: new Date().toISOString(),
    version: metadata.version || 1,
    size: packageData.byteLength,
    format: metadata.format || 'geojson',
    ...metadata
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.put(packageRecord);

    request.onsuccess = () => resolve(packageRecord);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get offline package from IndexedDB
 * @param {number} mapId - Map ID
 * @returns {Promise<Object|null>}
 */
export async function getOfflinePackage(mapId) {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.get(mapId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if offline package exists for a map
 * @param {number} mapId - Map ID
 * @returns {Promise<boolean>}
 */
export async function hasOfflinePackage(mapId) {
  const pkg = await getOfflinePackage(mapId);
  return pkg !== null;
}

/**
 * Delete offline package from IndexedDB
 * @param {number} mapId - Map ID
 */
export async function deleteOfflinePackage(mapId) {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.delete(mapId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all cached offline packages
 * @returns {Promise<Array>}
 */
export async function getAllOfflinePackages() {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const objectStore = transaction.objectStore(STORE_NAME);
    const request = objectStore.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get total storage used by offline packages
 * @returns {Promise<number>} - Size in bytes
 */
export async function getTotalStorageUsed() {
  const packages = await getAllOfflinePackages();
  return packages.reduce((total, pkg) => total + (pkg.size || 0), 0);
}

/**
 * Parse GeoJSON package from ArrayBuffer
 * @param {ArrayBuffer} data - Package data
 * @returns {Object} - Parsed package
 */
export function parseOfflinePackage(data) {
  const text = new TextDecoder('utf-8').decode(data);
  return JSON.parse(text);
}

/**
 * Parse MVT package from ArrayBuffer
 * @param {ArrayBuffer} data - Package data
 * @returns {Object} - Parsed package with header and tile index
 */
export function parseMvtPackage(data) {
  const view = new DataView(data);
  const headerLength = view.getUint32(0, true);
  const headerBytes = new Uint8Array(data, 4, headerLength);
  const headerText = new TextDecoder('utf-8').decode(headerBytes);
  const header = JSON.parse(headerText);
  const tilesOffset = 4 + headerLength;
  const tileIndex = header.tile_index || [];
  const tileIndexMap = new Map();

  tileIndex.forEach((entry) => {
    tileIndexMap.set(`${entry.z}/${entry.x}/${entry.y}`, entry);
  });

  return {
    header,
    tilesOffset,
    tileIndex,
    tileIndexMap,
    buffer: data
  };
}

/**
 * Get MVT tile data from a parsed package
 * @param {Object} packageData - Parsed MVT package
 * @param {number} z
 * @param {number} x
 * @param {number} y
 * @returns {ArrayBuffer|null}
 */
export function getMvtTileData(packageData, z, x, y) {
  if (!packageData?.tileIndexMap) return null;
  const entry = packageData.tileIndexMap.get(`${z}/${x}/${y}`);
  if (!entry) return null;

  const start = packageData.tilesOffset + entry.offset;
  const end = start + entry.size;
  return packageData.buffer.slice(start, end);
}
